import {
  AppCard,
  Button,
  Icon,
  InfoField,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiPencil } from "@mdi/js";
import { Box, CircularProgress, Typography } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  getShowOtherMasterdataServicesRequestQueryKey,
  useShowOtherMasterdataServicesRequest,
} from "../../../../../__generated__/orval/endpoints/other-masterdata-services-requests/other-masterdata-services-requests";
import { MmHistoryLogs } from "../../../../../components/molecules/MmHistoryLogs";
import { MmRequestAttachments } from "../../../../../components/molecules/MmRequestAttachments";
import { MmRequestHeader } from "../../../../../components/molecules/MmRequestHeader";
import { MmRequestComments } from "../../../../../components/molecules/MmRequestsComments";
import {
  MmRequestTabs,
  type MmRequestTabValue,
} from "../../../../../components/molecules/MmRequestTabs";
import { useAuth } from "../../../../../hooks/useAuth";
import { mmRequestTabsSearchSchema } from "../../mm_requests/-schemas/mmRequestTabsSearchSchema";

const ShowOtherMasterdataServicesRequest: React.FC = () => {
  const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const { user } = useAuth();
  const { otherMasterdataServicesRequestId } = Route.useParams();
  const { tab } = Route.useSearch();
  const id = Number(otherMasterdataServicesRequestId);

  const { data, isLoading, isError } =
    useShowOtherMasterdataServicesRequest(id);

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(t("mmRequests.fetchError"), { variant: "error" });
    }
  }, [isError, t]);

  const handleTabChange = useCallback(
    (newValue: MmRequestTabValue) => {
      navigate({
        replace: true,
        resetScroll: false,
        search: (prev) => ({
          ...prev,
          tab: newValue,
        }),
      });
    },
    [navigate],
  );

  const handleEdit = useCallback(() => {
    navigate({
      params: { otherMasterdataServicesRequestId },
      to: "/vbuyer/other_masterdata_services_requests/$otherMasterdataServicesRequestId/edit",
    });
  }, [otherMasterdataServicesRequestId, navigate]);

  const mmRequest = data?.mm_request;
  const mmRequestId = mmRequest?.id;
  const isCompleted = mmRequest?.process_instance?.completed ?? false;
  const mmRequestWithPreviousTicket = mmRequest as
    | (typeof mmRequest & { previous_ticket_id?: number | null })
    | undefined;

  const materialNumber = useMemo(() => {
    if (!Array.isArray(mmRequest?.matnumber)) {
      return "";
    }

    return mmRequest.matnumber.filter((value) => value.length > 0).join(", ");
  }, [mmRequest?.matnumber]);

  const userCanEdit = useMemo(() => {
    if (!user || !mmRequest) return false;

    return (
      (user.roles_names.includes("Edit material requests") ||
        user.id === mmRequest.assigned_to?.id) &&
      !mmRequest.process_instance?.completed
    );
  }, [mmRequest, user]);

  const title = mmRequestId
    ? `${t("common.otherMasterdataServicesRequest")} #${mmRequestId}`
    : t("common.otherMasterdataServicesRequest");

  if (isLoading) {
    return (
      <PageContainer showBackButton title={title}>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer showBackButton title={title}>
      {!!mmRequest && (
        <MmRequestHeader
          id={mmRequest.id}
          keysToInvalidate={[
            ...getShowOtherMasterdataServicesRequestQueryKey(id),
          ]}
        />
      )}

      <AppCard>
        <MmRequestTabs onChange={handleTabChange} value={tab} />
        <Box sx={{ marginTop: "32px" }}>
          {tab === "details" && mmRequest && (
            <AppCard
              header={
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="h6">{t("details")}</Typography>
                  {userCanEdit && (
                    <Button
                      onClick={handleEdit}
                      startIcon={<Icon icon={mdiPencil} />}
                      variant="outlined"
                    >
                      {t("common.edit")}
                    </Button>
                  )}
                </Box>
              }
            >
              <InfoField
                label={t("issuesOfRfq.previousTicket")}
                text={
                  mmRequestWithPreviousTicket?.previous_ticket_id
                    ? String(mmRequestWithPreviousTicket.previous_ticket_id)
                    : "-"
                }
              />
              <InfoField
                label={t("mmRequestChild.requesterField")}
                text={mmRequest.requester_field || "-"}
              />
              <InfoField
                label={t("mmRequestChild.quantityOfMaterials")}
                text={mmRequest.quantity_of_materials || "-"}
              />
              <InfoField
                label={t("common.materialNumber")}
                text={materialNumber || "-"}
              />
              <InfoField
                label={t("mmRequestChild.subject")}
                text={mmRequest.subject || "-"}
              />
              <InfoField
                label={t("mmRequestChild.description")}
                text={mmRequest.description || "-"}
                variant="boxed"
              />
              <InfoField
                label={t("mmRequestChild.isRepetro")}
                text={
                  data?.is_repetro === "yes"
                    ? t("yes")
                    : data?.is_repetro === "no"
                      ? t("no")
                      : "-"
                }
              />
            </AppCard>
          )}
          {tab === "attachments" && mmRequestId && (
            <MmRequestAttachments
              isCompleted={isCompleted}
              mmRequestId={mmRequestId}
            />
          )}
          {tab === "comments" && mmRequestId && (
            <MmRequestComments
              isCompleted={isCompleted}
              mmRequestId={mmRequestId}
            />
          )}
          {tab === "history" && mmRequestId && (
            <MmHistoryLogs mmRequestId={mmRequestId} />
          )}
        </Box>
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/other_masterdata_services_requests/$otherMasterdataServicesRequestId/",
)({
  component: ShowOtherMasterdataServicesRequest,
  validateSearch: mmRequestTabsSearchSchema,
});
